
import { supabase } from '@/integrations/supabase/client';
import { UserRole } from '@/types/user-roles';

export interface SignUpData {
  firstName: string;
  lastName: string;
  role: UserRole;
  organizationName?: string;
  selectedProviderId?: string;
}

export const signUp = async (
  email: string,
  password: string,
  userData: SignUpData
) => {
  // Note: reCAPTCHA has been removed from this application. Use invisible protections instead.
  // Sign up with Supabase but don't auto-confirm email
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        first_name: userData.firstName,
        last_name: userData.lastName,
        role: userData.role,
        organization_name: userData.organizationName
      },
      emailRedirectTo: `${window.location.origin}/verify-email`
    }
  });

  if (error) throw error;

  // If signup successful, send custom verification email
  if (data.user && !data.user.email_confirmed_at) {
    console.log('📧 Sending verification email for user:', data.user.id, 'email:', email);
    try {
      const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-email', {
        body: { 
          email,
          userId: data.user.id,
          type: 'email_verification'
        }
      });

      if (emailError) {
        console.error('❌ Error sending verification email:', emailError);
        // Don't fail the signup if email sending fails
      } else {
        console.log('✅ Verification email sent successfully:', emailResult);
      }
    } catch (emailError) {
      console.error('❌ Exception sending verification email:', emailError);
      // Don't fail the signup if email sending fails
    }
  } else {
    console.log('⚠️ User already confirmed or no user created:', {
      userExists: !!data.user,
      emailConfirmed: data.user?.email_confirmed_at
    });
  }

  // Create PEO representative link if provider was selected
  if (data.user && userData.role === 'peo' && userData.selectedProviderId) {
    try {
      await supabase.functions.invoke('create-user-peo-link', {
        body: {
          userId: data.user.id,
          providerId: userData.selectedProviderId,
          verificationStatus: 'pending'
        }
      });
      console.log('✅ PEO representative link created for user:', data.user.id);
    } catch (error) {
      console.error('❌ Error creating PEO representative link:', error);
      // Don't fail the signup if linking fails
    }
  }

  // Send admin notification for new user registration
  if (data.user) {
    try {
      if (userData.role === 'peo') {
        const adminUrl = `${typeof window !== 'undefined' && (window as any).location ? (window as any).location.origin : 'https://thepeoapp.com'}/admin/peo-verifications`;
        const { error: peoNotifyError } = await supabase.functions.invoke('send-email', {
          body: {
            type: 'peo_verification_request',
            userData: {
              email: data.user.email,
              firstName: userData.firstName,
              lastName: userData.lastName,
              role: userData.role,
              userId: data.user.id,
              signupDate: new Date().toISOString(),
              requestedAt: new Date().toISOString(),
              adminUrl
            }
          }
        });
        if (peoNotifyError) {
          console.error('Error sending PEO verification request email:', peoNotifyError);
        }
      } else {
        const { error: adminEmailError } = await supabase.functions.invoke('send-email', {
          body: {
            email: 'info@thepeoapp.com',
            type: 'admin_new_user_notification',
            userData: {
              email: data.user.email,
              firstName: userData.firstName,
              lastName: userData.lastName,
              role: userData.role,
              userId: data.user.id,
              signupDate: new Date().toISOString()
            }
          }
        });
        if (adminEmailError) {
          console.error('Error sending admin notification email:', adminEmailError);
        }
      }
    } catch (adminEmailError) {
      console.error('Error sending admin notification email:', adminEmailError);
      // Don't fail the signup if admin email fails
    }
  }

  return { data, error };
};

export const signIn = async (email: string, password: string) => {
  return await supabase.auth.signInWithPassword({
    email,
    password
  });
};

export const signInWithPhone = async (phone: string) => {
  return await supabase.auth.signInWithOtp({
    phone,
  });
};

export const verifyOtp = async (phone: string, token: string, userData?: SignUpData) => {
  const verifyOptions: any = {
    phone,
    token,
    type: 'sms'
  };

  // If userData is provided, include it in the verification options
  if (userData) {
    verifyOptions.options = {
      data: {
        first_name: userData.firstName || '',
        last_name: userData.lastName || '',
        role: userData.role,
        organization_name: userData.organizationName || '',
        profile_completed: true
      }
    };
  }

  const result = await supabase.auth.verifyOtp(verifyOptions);
  
  // Send admin notifications for phone users if verification is successful
  if (!result.error && result.data.user && userData) {
    try {
      if (userData.role === 'peo') {
        const adminUrl = `${typeof window !== 'undefined' && (window as any).location ? (window as any).location.origin : 'https://thepeoapp.com'}/admin/peo-verifications`;
        const { error: peoNotifyError } = await supabase.functions.invoke('send-email', {
          body: {
            type: 'peo_verification_request',
            userData: {
              email: result.data.user.email || phone, // Use phone if no email
              firstName: userData.firstName,
              lastName: userData.lastName,
              role: userData.role,
              userId: result.data.user.id,
              signupDate: new Date().toISOString(),
              requestedAt: new Date().toISOString(),
              adminUrl,
              phone: phone // Include phone number for PEO verification
            }
          }
        });
        if (peoNotifyError) {
          console.error('Error sending PEO verification request email for phone user:', peoNotifyError);
        }
      } else {
        const { error: adminEmailError } = await supabase.functions.invoke('send-email', {
          body: {
            email: 'info@thepeoapp.com',
            type: 'admin_new_user_notification',
            userData: {
              email: result.data.user.email || phone, // Use phone if no email
              firstName: userData.firstName,
              lastName: userData.lastName,
              role: userData.role,
              userId: result.data.user.id,
              signupDate: new Date().toISOString(),
              phone: phone // Include phone number in admin notification
            }
          }
        });
        if (adminEmailError) {
          console.error('Error sending admin notification email for phone user:', adminEmailError);
        }
      }
    } catch (adminEmailError) {
      console.error('Error sending admin notification email for phone user:', adminEmailError);
      // Don't fail the verification if admin email fails
    }
  }

  return result;
};

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    return { error: null };
  } catch (error: any) {
    console.error("Error during signout:", error.message);
    return { error };
  }
};

export const getCurrentUser = async () => {
  const { data } = await supabase.auth.getUser();
  return data.user;
};

export const getUserProfile = async (userId: string) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
    
  if (error) throw error;
  return data;
};

export const getPEOVerificationStatus = async (userId: string) => {
  try {
    // Check if user has any verified PEO provider relationships
    const { data, error } = await supabase
      .from('peo_representatives')
      .select('verification_status')
      .eq('user_id', userId)
      .eq('verification_status', 'verified')
      .maybeSingle();

    if (error) {
      console.error('Error fetching PEO verification status:', error);
      return 'pending';
    }

    // Return 'approved' if user has verified provider relationship, 'pending' otherwise
    return data ? 'approved' : 'pending';
  } catch (error) {
    console.error('Error in getPEOVerificationStatus:', error);
    return 'pending';
  }
};

export const checkEmailVerificationStatus = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('email_verified')
      .eq('id', userId)
      .single();
      
    if (error) throw error;
    return data?.email_verified || false;
  } catch (error) {
    console.error("Error checking email verification status:", error);
    return false;
  }
};

export const resendVerificationEmail = async (email: string, userId: string) => {
  try {
    const { error } = await supabase.functions.invoke('send-email', {
      body: { 
        email,
        userId,
        type: 'email_verification'
      }
    });

    if (error) throw error;
    return { success: true };
  } catch (error: any) {
    console.error('Error resending verification email:', error);
    throw error;
  }
};
